/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

/** @typedef {import('../status/Status').StatusType} StatusType */

/**
 * Validation statuses.
 * @type {{ IDLE: StatusType, LOADING: StatusType, UNKNOWN: StatusType, SUCCESS: StatusType, ERROR: StatusType, WARNING: StatusType }}
 */

export const ValidationStatus = {
  IDLE: 'idle',
  LOADING: 'loading',
  UNKNOWN: 'unknown',
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning'
};

/**
 * Get a tooltip message for a given status and context.
 * @param {'feel' | 'json' | 'result'} context
 * @param {StatusType} status
 * @param {string | null} [message]
 * @returns {string}
 */
export const getTooltipMessage = (context, status, message = null) => {
  if (context === 'json') {
    switch (status) {
      case 'success':
        return 'Valid JSON';
      case 'error':
        return `Invalid JSON: ${message}`;
    }
  }

  if (context === 'feel') {
    switch (status) {
      case 'loading':
        return `Validating...`;
      case 'success':
        return 'Expression evaluated successfully';
      case 'warning':
        return `Expression evaluated with warnings: ${message}`;
      case 'error':
        return `Expression evaluation failed: ${message}`;
    }
  }

  if (context === 'result') {
    switch (status) {
      case 'success':
        return 'Expression evaluated successfully';
      case 'warning':
        return 'Expression evaluated with warnings';
      case 'error':
        return 'Expression evaluated with errors';
      case 'loading':
        return 'Evaluating expression...';
      case 'unknown':
        return 'Evaluation failed unexpectedly';
    }
  }

  return '';
};

/**
 * @param {string} rawTitle - The raw warning title to format.
 * @returns {string} The formatted warning title for display.
 */
export const formatWarningTitle = (rawTitle) => {
  if (!rawTitle) {
    return '';
  }

  return rawTitle
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * @param {string} value - The value to check.
 * @returns {boolean} The result of verification.
 */
export const isNonEmpty = (value) => value !== null && value !== undefined && value.trim() !== '';

/**
 * Sorts diagnostics by severity, prioritizing errors over warnings.
 *
 * @param {Array<{severity: string, message: string}>} diagnostics - The diagnostics to sort
 * @returns {Array<{severity: string, message: string}>} Sorted diagnostics with errors first
 */
export function sortDiagnosticsBySeverity(diagnostics) {
  return [...diagnostics].sort((a, b) => {
    if (a.severity === 'error' && b.severity !== 'error') {
      return -1;
    }
    if (a.severity !== 'error' && b.severity === 'error') {
      return 1;
    }
    return 0;
  });
}
